import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  input,
  signal,
  output,
} from '@angular/core';
import { ModalService } from '@app/core/services/modal/modal.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { InputModalComponent } from '@app/core/components/input-modal/input-modal.component';
import { ConfirmModalComponent } from '@app/core/components/confirm-modal/confirm-modal.component';
import { ColumnMapping } from '@app/core/components-openagri/agx-pivot-table/agx-pivot-table.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'ag-pivot-table-column-layout',
  templateUrl: './pivot-table-column-layout.component.html',
  styleUrls: ['./pivot-table-column-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule],
})
export class PivotTableColumnLayoutComponent implements OnInit {
  private modalService = inject(ModalService);
  private toast = inject(ToastService);

  DEFAULT_LAYOUT = 'Default';
  pivotTableLayouts: Map<string, ColumnMapping[]>;
  pivotTableColumns: ColumnMapping[];

  readonly localStorageKey = input<string>(undefined);

  /** Internal UI state only — no parent two-way binding. Sync via layoutChanged. */
  readonly activeLayoutName = signal(this.DEFAULT_LAYOUT);

  readonly defaultColumns = input<ColumnMapping[]>(undefined);

  readonly isLoading = input(false);

  readonly layoutChanged = output<string>();

  get pivotTableLayoutNames(): string[] {
    return Array.from(this.pivotTableLayouts.keys());
  }

  get usingDefaultColumnLayout(): boolean {
    return this.activeLayoutName() === this.DEFAULT_LAYOUT;
  }

  constructor() {}

  ngOnInit(): void {
    this.pivotTableLayouts = this.loadPivotLayouts();
    if (!this.activeLayoutName()) {
      this.activeLayoutName.set(this.DEFAULT_LAYOUT);
    }
    this.activatePivotTableLayout(this.activeLayoutName());
  }

  loadPivotLayouts(): Map<string, ColumnMapping[]> {
    const savedLayouts = this.getSavedLayoutsFromLocalStorage();

    // [pitto order management hack] 'if' clause commented out to ensure:
    // 1. 'default' layout always set properly, even with AG1 user buyer/seller change etc.
    // 2. 'state' property set to 'displayState' after recent changes necessary for Pitto
    // note - other saved layouts are still kept (and may need manual fixing)
    // TODO: refine the below or reinstate the 'if' when possible
    // if (savedLayouts.size === 0) {
    const defaultLayout = this.defaultColumns();
    savedLayouts.set(this.DEFAULT_LAYOUT, defaultLayout);
    this.savePivotLayouts(savedLayouts as Map<string, ColumnMapping[]>);
    // }
    return savedLayouts as Map<string, ColumnMapping[]>;
  }

  getSavedLayoutsFromLocalStorage(): Map<string, ColumnMapping[]> {
    const ls = localStorage.getItem(this.localStorageKey());

    let savedLayouts: Map<string, ColumnMapping[]>;
    if (!ls) {
      savedLayouts = new Map();
    } else {
      savedLayouts = new Map(JSON.parse(ls)) as Map<string, ColumnMapping[]>;
    }
    return savedLayouts;
  }

  savePivotLayouts(layouts: Map<string, ColumnMapping[]>) {
    localStorage.setItem(this.localStorageKey(), JSON.stringify([...layouts]));
  }

  activatePivotTableLayout(name: string) {
    this.activeLayoutName.set(name);
    this.pivotTableColumns = [...this.pivotTableLayouts.get(name)];
    this.layoutChanged.emit(name);
  }

  showSaveLayoutModal(): InputModalComponent {
    const modalInstance = this.modalService.showInput({
      title: 'New Column Layout',
      field: {
        inputType: 'text',
        inputLabel: 'Name',
        required: true,
      },
      confirmBtnClass: 'is-link',
      confirmBtnText: 'Save Layout',
      onConfirm: (name: string) => {
        name = name.trim();
        const alreadyExists =
          this.pivotTableLayoutNames
            .map((pName) => pName.toLowerCase())
            .indexOf(name.toLowerCase()) > -1;

        if (alreadyExists) {
          modalInstance.addCustomError(
            'Name',
            `Layout "${name}" already exists`,
          );
          return;
        }

        this.pivotTableLayouts.set(name, this.pivotTableColumns);
        this.savePivotLayouts(this.pivotTableLayouts);
        this.activatePivotTableLayout(name);

        modalInstance.hide();
        this.toast.showToast('Column Layout Saved');
      },
    });
    return modalInstance;
  }

  showDeleteLayoutModal(name: string): ConfirmModalComponent {
    const modalInstance = this.modalService.showConfirm({
      title: 'Delete Column Layout?',
      content: `Are you sure you want to delete column layout "${name}"?`,
      confirmBtnText: 'Delete Layout',
      confirmBtnClass: 'is-danger',
      canCancel: true,
      onConfirm: () => {
        this.pivotTableLayouts.delete(name);
        this.savePivotLayouts(this.pivotTableLayouts);

        if (this.activeLayoutName() === name) {
          this.activatePivotTableLayout(this.DEFAULT_LAYOUT);
        }

        modalInstance.hide();
        this.toast.showToast('Column Layout Deleted');
      },
    });
    return modalInstance;
  }

  showUpdateLayoutModal(name: string): ConfirmModalComponent {
    const modalInstance = this.modalService.showConfirm({
      title: 'Update Column Layout?',
      content: `Are you sure you want to update column layout "${name}"? This will overwrite the layout.`,
      confirmBtnText: 'Update Layout',
      confirmBtnClass: 'is-link',
      canCancel: true,
      onConfirm: () => {
        this.pivotTableLayouts.set(name, this.pivotTableColumns);
        this.savePivotLayouts(this.pivotTableLayouts);
        this.activatePivotTableLayout(name);
        modalInstance.hide();
        this.toast.showToast('Column Layout Updated');
      },
    });
    return modalInstance;
  }
}
